import Vue from "vue";
import { Store } from "vuex";
import { SidebarState, SidebarType } from "@/views/sidebar/sidebar.types";

declare module "vue/types/vue" {
  interface Vue {
    $sidebar: SidebarPlugin
  }
}

interface SidebarPlugin {
  open: {
    (options: SidebarState): void;
    (type: SidebarState["type"], item?: SidebarState["item"], options?: Omit<SidebarState, "type" | "item">): void;
  };
  close(options?: SidebarState["options"]): void;
  resetHistory(): void;
  type: SidebarType;
  item: any;
  subItem?: any;
  title?: SidebarState["title"];
  options?: SidebarState["options"];
  state: SidebarState;
  isOpen: boolean;
  size: string;
}

interface InstallerOptions {
  store: Store<any>;
}

const install = (vue: typeof Vue, { store }: InstallerOptions) => {
  const open: SidebarPlugin["open"] = (...args: any[]) => {
    if (typeof args[0] === "object") {
      const [state] = args;
      store.commit("Sidebar/setSidebarState", state);
    } else {
      const [type, item, options] = args;
      store.commit("Sidebar/setSidebarState", { type, item, ...options });
    }
  };
  const close: SidebarPlugin["close"] = (options: SidebarState["options"] = {}) => open(SidebarType.None, {}, { options });
  const resetHistory = (): void => store.commit("Sidebar/resetHistory");
  const getType = (): SidebarPlugin["type"] => store.getters["Sidebar/type"];
  const getItem = (): SidebarPlugin["item"] => store.getters["Sidebar/item"];
  const setItem = (item: SidebarPlugin["item"]) => store.commit("Sidebar/setSidebarItem", item);
  const getTitle = (): SidebarPlugin["title"] => store.getters["Sidebar/title"];
  const getOptions = (): SidebarPlugin["options"] => store.getters["Sidebar/options"];
  const setOptions = (options: SidebarPlugin["options"]) => store.commit("Sidebar/setSidebarOptions", options);
  const getState = (): SidebarPlugin["state"] => store.getters["Sidebar/curState"];
  const getPrevState = (): SidebarPlugin["state"] => store.getters["Sidebar/prevState"];
  const getSize = (): SidebarPlugin["size"] => store.getters["Sidebar/size"];
  const setSize = (size: SidebarPlugin["size"]) => store.commit("Sidebar/setSidebarSize", size);

  vue.prototype.$sidebar = {
    open,
    close,
    resetHistory,
    get type() {
      return getType();
    },
    get item() {
      return getItem();
    },
    set item(value) {
      setItem(value);
    },
    get title() {
      return getTitle();
    },
    get options() {
      return getOptions();
    },
    set options(value) {
      setOptions(value);
    },
    get state() {
      return getState();
    },
    get prevState() {
      return getPrevState();
    },
    get isOpen() {
      return getType() !== SidebarType.None;
    },
    get size() {
      return getSize();
    },
    set size(value: string) {
      setSize(value);
    },
  };
};

const plugin = { install };

export default plugin;